import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useEffect } from 'react';
import { useAuth } from 'react-oidc-context';
import { useNavigate } from 'react-router-dom';
import { useBootstrap } from '@/auth/bootstrap-context';
import SwitchField from '@/common/components/forms/switch-field';
import { useCloudSyncPaused } from '@/hooks/use-cloud-sync-paused';
import { ALL_TUTORIAL_KEYS } from '@/common/components/tutorial-spotlight/keys';
import ImportSection from '@/modules/import/import-section';
import { updateProfile } from '@/modules/profile/profile-api';
import { createCheckout, cancelSubscription, resumeSubscription, } from '@/modules/subscription/subscription-api';
import SubscriptionSection from '@/modules/subscription/subscription-section';
import { useSubscription } from '@/modules/subscription/use-subscription';
import { clearAllBudgets, getDbmVersion } from '@/storage/storage';
import { useAppState } from '@/store/context';
import ModalClearData from './modal-clear-data';
import ModalResetHints from './modal-reset-hints';
const AVATAR_OPTIONS = [
    '🙂',
    '😎',
    '🤠',
    '🥸',
    '🤓',
    '😄',
    '🤩',
    '😇',
    '🧐',
    '😏',
    '🧑',
    '👩',
    '👨',
    '🧔',
    '👦',
    '👧',
    '🧓',
    '👴',
    '👵',
    '🤖',
    '🦊',
    '🐱',
    '🐼',
    '🦁',
    '🐺',
    '🐨',
    '🐸',
    '🦋',
    '🌟',
    '💎',
];
function ProfilePage() {
    const { state, dispatch } = useAppState();
    const navigate = useNavigate();
    const auth = useAuth();
    const bootstrap = useBootstrap();
    const [editingName, setEditingName] = useState(false);
    const [nameVal, setNameVal] = useState(state.profile.name);
    const [showPicker, setShowPicker] = useState(false);
    const [showClearModal, setShowClearModal] = useState(false);
    const [showResetHintsModal, setShowResetHintsModal] = useState(false);
    const [dbmVersion, setDbmVersion] = useState(null);
    // Реальная идентичность пользователя — серверный userId из bootstrap
    // (стабилен между устройствами). state.profile.id — локальный случайный UUID,
    // показывать его как «ID пользователя» нельзя. Fallback на локальный только
    // офлайн, когда bootstrap недоступен.
    const displayUserId = bootstrap.data?.profile.userId ?? state.profile.id;
    const subscription = useSubscription();
    const { paused } = useCloudSyncPaused();
    const [busyPlan, setBusyPlan] = useState(null);
    const [canceling, setCanceling] = useState(false);
    const [resuming, setResuming] = useState(false);
    useEffect(() => {
        getDbmVersion().then(setDbmVersion);
    }, []);
    async function startCheckout(planCode) {
        setBusyPlan(planCode);
        try {
            const { confirmationUrl } = await createCheckout(planCode);
            window.location.href = confirmationUrl;
        }
        catch {
            setBusyPlan(null);
        }
    }
    async function cancelMySubscription() {
        setCanceling(true);
        try {
            await cancelSubscription();
            subscription.reload();
            // Политика могла измениться (если доступ отзывается сразу) — перечитываем.
            await bootstrap.refresh().catch(() => { });
        }
        finally {
            setCanceling(false);
        }
    }
    async function resumeMySubscription() {
        setResuming(true);
        try {
            await resumeSubscription();
            subscription.reload();
            await bootstrap.refresh().catch(() => { });
        }
        finally {
            setResuming(false);
        }
    }
    const totalTx = state.budgets.reduce((s, b) => s + b.transactions.length, 0);
    function saveName() {
        const trimmed = nameVal.trim();
        const name = trimmed || state.profile.name;
        dispatch({ type: 'UPDATE_PROFILE', updates: { name } });
        if (!trimmed)
            setNameVal(state.profile.name);
        setEditingName(false);
        // Синхронизируем на сервер (best-effort — офлайн не должен ломать локальную правку)
        updateProfile({ displayName: name }).catch(() => { });
    }
    function selectAvatar(avatar) {
        dispatch({ type: 'UPDATE_PROFILE', updates: { avatar } });
        setShowPicker(false);
        updateProfile({ avatar }).catch(() => { });
    }
    return (_jsxs("div", { className: "profile-page", children: [_jsxs("div", { className: "profile-header", children: [_jsxs("button", { className: "btn-back", onClick: () => navigate(-1), children: ["\u2190 ", _jsx("span", { children: "\u041D\u0430\u0437\u0430\u0434" })] }), _jsx("span", { className: "profile-header-title", children: "\u041F\u0440\u043E\u0444\u0438\u043B\u044C" }), _jsx("div", { className: "profile-header-spacer" })] }), _jsxs("div", { className: "profile-content", children: [_jsxs("div", { className: "profile-avatar-section", children: [_jsx("button", { className: "profile-avatar-btn", onClick: () => setShowPicker((p) => !p), children: state.profile.avatar }), _jsx("button", { className: "profile-avatar-change", onClick: () => setShowPicker((p) => !p), children: showPicker ? 'Закрыть' : 'Изменить аватар' }), showPicker && (_jsx("div", { className: "profile-avatar-picker", children: AVATAR_OPTIONS.map((a) => (_jsx("button", { className: `profile-avatar-opt${state.profile.avatar === a ? ' selected' : ''}`, onClick: () => selectAvatar(a), children: a }, a))) }))] }), _jsx("div", { className: "profile-name-section", children: editingName ? (_jsx("input", { className: "profile-name-input", value: nameVal, autoFocus: true, onChange: (e) => setNameVal(e.target.value), onBlur: saveName, onKeyDown: (e) => {
                                if (e.key === 'Enter')
                                    saveName();
                                if (e.key === 'Escape') {
                                    setNameVal(state.profile.name);
                                    setEditingName(false);
                                }
                            } })) : (_jsxs("button", { className: "profile-name", onClick: () => {
                                setNameVal(state.profile.name);
                                setEditingName(true);
                            }, children: [state.profile.name, _jsx("span", { className: "profile-name-hint", children: "\u270E" })] })) }), bootstrap.data?.profile.email && (_jsx("div", { style: { fontSize: 13, color: 'var(--text-2)', marginTop: 4 }, children: bootstrap.data.profile.email })), _jsxs("div", { style: { fontSize: 11, color: 'var(--text-3, #999)', marginTop: 4 }, children: [_jsxs("span", { style: { userSelect: 'all' }, children: ["ID: ", displayUserId] }), dbmVersion && _jsxs("span", { style: { marginLeft: 12 }, children: ["Schema: ", dbmVersion] })] }), _jsxs("div", { className: "profile-stats", children: [_jsxs("div", { className: "profile-stat", children: [_jsx("span", { className: "profile-stat-value", children: state.budgets.length }), _jsx("span", { className: "profile-stat-label", children: "\u0411\u044E\u0434\u0436\u0435\u0442\u043E\u0432" })] }), _jsxs("div", { className: "profile-stat", children: [_jsx("span", { className: "profile-stat-value", children: totalTx }), _jsx("span", { className: "profile-stat-label", children: "\u041E\u043F\u0435\u0440\u0430\u0446\u0438\u0439" })] })] }), _jsx(SwitchField, { label: "\u041F\u043E\u043A\u0430\u0437\u044B\u0432\u0430\u0442\u044C \u0441\u043B\u0435\u0434\u0443\u044E\u0449\u0438\u0439 \u043C\u0435\u0441\u044F\u0446", description: "\u041F\u0440\u0438\u0433\u043B\u0443\u0448\u0451\u043D\u043D\u044B\u0435 \u043A\u0430\u0440\u0442\u043E\u0447\u043A\u0438 \u0434\u043D\u0435\u0439 \u0441\u043B\u0435\u0434\u0443\u044E\u0449\u0435\u0433\u043E \u043C\u0435\u0441\u044F\u0446\u0430 \u0432 \u043A\u043E\u043D\u0446\u0435 \u043A\u0430\u043B\u0435\u043D\u0434\u0430\u0440\u043D\u043E\u0439 \u0441\u0435\u0442\u043A\u0438", isOn: state.profile.showNextMonth ?? true, onToggle: (val) => dispatch({ type: 'UPDATE_PROFILE', updates: { showNextMonth: val } }), style: {
                            marginTop: 28,
                            padding: '12px 0',
                            borderTop: '1px solid var(--border-light)',
                            borderBottom: '1px solid var(--border-light)',
                        } }), _jsx(SubscriptionSection, { data: subscription.data, loading: subscription.loading, error: subscription.error, paused: paused, busyPlan: busyPlan, canceling: canceling, resuming: resuming, onCheckout: startCheckout, onCancel: cancelMySubscription, onResume: resumeMySubscription }), _jsx(ImportSection, {}), _jsx("button", { className: "btn-secondary", style: {
                            marginTop: 36,
                            padding: '9px 18px',
                            fontSize: 14,
                            fontWeight: 500,
                            borderRadius: 6,
                            background: 'var(--border)',
                            color: 'var(--text)',
                        }, onClick: () => setShowResetHintsModal(true), children: "\u0421\u0431\u0440\u043E\u0441\u0438\u0442\u044C \u043F\u043E\u0434\u0441\u043A\u0430\u0437\u043A\u0438" }), _jsxs("button", { className: "btn-secondary", style: {
                            marginTop: 48,
                            padding: '9px 18px',
                            fontSize: 14,
                            fontWeight: 500,
                            borderRadius: 6,
                            background: 'var(--danger-bg, #fee)',
                            color: 'var(--danger, #c00)',
                        }, onClick: () => setShowClearModal(true), children: [_jsxs("svg", { viewBox: "0 0 20 20", fill: "none", width: "16", height: "16", style: { marginRight: 6, verticalAlign: -2 }, children: [_jsx("path", { d: "M3 5h14", stroke: "currentColor", strokeWidth: "1.6", strokeLinecap: "round" }), _jsx("path", { d: "M7 5V4a2 2 0 012-2h2a2 2 0 012 2v1", stroke: "currentColor", strokeWidth: "1.6" }), _jsx("path", { d: "M5 5l1 12a2 2 0 002 2h4a2 2 0 002-2l1-12", stroke: "currentColor", strokeWidth: "1.6" }), _jsx("path", { d: "M8.5 9v5M11.5 9v5", stroke: "currentColor", strokeWidth: "1.6", strokeLinecap: "round" })] }), "\u041E\u0447\u0438\u0441\u0442\u0438\u0442\u044C \u043B\u043E\u043A\u0430\u043B\u044C\u043D\u0443\u044E \u0431\u0430\u0437\u0443"] }), _jsx("button", { className: "btn-secondary", style: {
                            marginTop: 16,
                            padding: '9px 18px',
                            fontSize: 14,
                            fontWeight: 500,
                            borderRadius: 6,
                            background: 'var(--border)',
                            color: 'var(--text-2)',
                        }, onClick: () => auth.signoutRedirect(), children: "\u0412\u044B\u0439\u0442\u0438" })] }), showResetHintsModal && (_jsx(ModalResetHints, { onClose: () => setShowResetHintsModal(false), onConfirm: () => {
                    ALL_TUTORIAL_KEYS.forEach((k) => localStorage.removeItem(k));
                    dispatch({ type: 'UPDATE_PROFILE', updates: { seenTutorials: [] } });
                    updateProfile({ seenTutorials: [] }).catch(() => { });
                    setShowResetHintsModal(false);
                    navigate('/app');
                } })), showClearModal && (_jsx(ModalClearData, { userId: displayUserId, onClose: () => setShowClearModal(false), onConfirm: async () => {
                    await clearAllBudgets();
                    dispatch({ type: 'CLEAR_LOCAL_DATA' });
                    setShowClearModal(false);
                    navigate('/app');
                } }))] }));
}
export default ProfilePage;
